import React, { useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import Link from 'next/link';
import posthog from 'posthog-js';
import Image from 'next/image';
import { Caption } from '../../../Typography';
import config from '../../../../../config.json';
import { useWindowSize } from '../../../../hooks/useWindowSize';
import { useStore } from '../../../../store';
import { isUserLoggedIn } from '../../../../utils/analytics';

const AccordionWrapper = styled.div`
  margin: 6px 0px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    margin: 8px 24px 8px 0px;
  }
`;

const AccordionSection = styled.div``;

const AccordionHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    width: 106px;
  }
`;

export const AccordionArrow = styled(Image)<{ $visible: boolean }>`
  ${({ $visible }) => $visible && `transform: rotate(180deg);`}
  transition: transform 0.25s ease-in-out;
`;

const AccordionBody = styled.div<{ $visible: boolean }>`
  max-height: ${({ $visible }) => ($visible ? '1000px' : '0')};
  height: auto;
  overflow: hidden;
  transition: max-height 0.25s ease-in-out;
  padding-left: 12px;
`;

const AccordionBodyHeading = styled(Caption)<{
  active?: boolean;
  last?: boolean;
}>`
  display: block;
  margin: 6px 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }

  ${({ active }) => active && `font-weight: bold;`}

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    margin-right: ${({ last }) => (last ? 16 : 24)}px;
  }

  &:last-child {
    margin-bottom: 0px;
  }
`;

const AccordionSectionTitle = styled(Caption)<{ active: boolean | undefined }>`
  ${({ active }) => active && `font-weight: bold;`}
`;

export type AccordionSectionType = {
  title: string;
  subtitle?: React.ReactNode;
  body: AccordionBodyType[];
};

type AccordionBodyType = {
  title?: string;
  bodyText?: string;
  body?: React.ReactNode;
};

type SidebarAccordionProps = {
  sidebarItems: SidebarItemType[];
  sidebarProductTypeHandle: string;
  sidebarProductTypeTitle: string;
  handle?: string;
  resetOpenSection?: () => void;
};

const SidebarAccordion: React.FC<SidebarAccordionProps> = ({
  sidebarItems,
  sidebarProductTypeHandle,
  sidebarProductTypeTitle,
  handle,
  resetOpenSection,
}: SidebarAccordionProps) => {
  const { plushCollectionHandles, apparelCollectionHandles, topPicksCollectionHandles } = config;
  const { state } = useStore();

  const [open, setOpen] = useState<boolean>(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  const size = useWindowSize();
  const theme = useTheme();

  useEffect(() => {
    if (!size.width) {
      return;
    }
    // eslint-disable-next-line react/destructuring-assignment
    const isMobileViewport = size.width <= theme.breakpoints.largeTablet;
    setIsMobileViewport(isMobileViewport);
  }, [size]);

  const renderedSidebarItems = useMemo(() => {
    if (sidebarProductTypeHandle === plushCollectionHandles.plushies) {
      return sidebarItems.filter((item) => Object.values(plushCollectionHandles).includes(item.handle || ''));
    }
    if (sidebarProductTypeHandle === apparelCollectionHandles.apparel) {
      return sidebarItems.filter((item) => Object.values(apparelCollectionHandles).includes(item.handle || ''));
    }

    return sidebarItems.filter((item) => Object.values(topPicksCollectionHandles).includes(item.handle || ''));
  }, [
    sidebarItems,
    sidebarProductTypeHandle,
    plushCollectionHandles,
    apparelCollectionHandles,
    topPicksCollectionHandles,
  ]);

  const isActive = renderedSidebarItems.some((item) => item.handle === handle);

  useEffect(() => {
    if (handle) {
      setOpen(isActive);
    }
  }, [handle, isActive]);

  const handleClick = () => {
    if (isMobileViewport) {
      return;
    }
    if (open) {
      setOpen(false);
      resetOpenSection?.();
    } else {
      setOpen(true);
      posthog.capture('collection_sidebar_accordion_click', {
        sidebarProductTypeHandle,
        is_logged_in: isUserLoggedIn(state.user),
      });
    }
  };

  const handleSidebarItemClick = (item: SidebarItemType) => {
    posthog.capture('collection_sidebar_item_click', {
      sidebarProductTypeHandle,
      itemTitle: item.title,
      itemHandle: item.handle,
      is_logged_in: isUserLoggedIn(state.user),
    });
  };

  return renderedSidebarItems.length > 0 ? (
    <AccordionWrapper>
      <AccordionSection key={sidebarProductTypeHandle}>
        <AccordionHead
          onClick={(e) => {
            // Prevent toggling if the click originated from inside a link
            const isLinkClick = (e.target as HTMLElement).closest('a');
            if (isLinkClick) return;
            handleClick();
          }}
        >
          <Link href={`/shop/${sidebarProductTypeHandle}`} passHref legacyBehavior>
            {/* Need the separate anchor tag so that we can disable clicks on it from opening/closing accordion */}
            <a href={`/shop/${sidebarProductTypeHandle}`}>
              <AccordionSectionTitle active={isActive}>{sidebarProductTypeTitle}</AccordionSectionTitle>
            </a>
          </Link>
          {!isMobileViewport && (
            <AccordionArrow
              $visible={open}
              src="/assets/icons/dropdown-icon.svg"
              width={20}
              height={11}
              alt="Dropdown Icon"
            />
          )}
        </AccordionHead>
        {!isMobileViewport && (
          <AccordionBody $visible={open}>
            {renderedSidebarItems.map((item) => (
              <Link key={item.title} href={`/shop/${item.handle}`} passHref>
                <AccordionBodyHeading
                  active={handle?.toLowerCase() === item.handle.toLowerCase()}
                  key={item.title}
                  onClick={() => handleSidebarItemClick(item)}
                >
                  {item.title}
                </AccordionBodyHeading>
              </Link>
            ))}
          </AccordionBody>
        )}
      </AccordionSection>
    </AccordionWrapper>
  ) : (
    <></>
  );
};

export default SidebarAccordion;
